import { devProxyRouteForApiPath, fetchWithDevProxyFallback } from '../api/fetchWithDevProxyFallback';
import { extractAccessTokenFromLoginJson } from '../api/authLoginResponse';
import { isSuccessfulHttpStatus } from '../api/apiFetch';
import { friendlyHttpFailureSummary } from '../api/httpErrorFormat';
import { extractRefreshTokenFromUnknown, clearRefreshUnavailable404 } from '../api/authRefresh';
import { decodeJwtPayload } from '../auth/jwtPayload';
import devGoogleOAuthClientId from '../devGoogleOAuthClientId.json';
import { isMainPortalLoginType, prefersGoogleRegisterFirst, supportsGoogleLiteRegister } from './authEntryPolicy';
import {
  ASSET_OWNER_GOOGLE_LOGIN_PATHS,
  ASSET_OWNER_GOOGLE_REGISTER_PATHS,
} from './assetOwnerApiPaths';
import { canonicalAssetOwnerLoginType, type LoginOption } from './loginConstants';

export function resolveGoogleOAuthClientId(): string | null {
  const fromEnv = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID?.trim();
  if (fromEnv) {
    return fromEnv;
  }
  if (process.env.NODE_ENV === 'development') {
    const fromDevFile = devGoogleOAuthClientId.clientId?.trim();
    if (fromDevFile) {
      return fromDevFile;
    }
  }
  return null;
}

function googleLoginPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_GOOGLE_LOGIN_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['login/PrivateClient/google', 'login/google'],
    'business client': ['login/BusinessClient/google', 'login/clients/google', 'login/google'],
    'business worker': ['login/BusinessWorker/google', 'login/clients/google', 'login/google'],
    'asset owner': [...ASSET_OWNER_GOOGLE_LOGIN_PATHS],
    'business asset owner': [...ASSET_OWNER_GOOGLE_LOGIN_PATHS],
    'private asset owner': [...ASSET_OWNER_GOOGLE_LOGIN_PATHS],
  };
  const paths = [configured, ...(byRole[loginType] ?? ['login/google'])].filter(
    (value): value is string => Boolean(value?.trim()),
  );
  return Array.from(new Set(paths));
}

export function googleLoginPathCandidatesForTests(loginType: LoginOption): string[] {
  return googleLoginPathCandidates(loginType);
}

function googleRegisterPathCandidates(loginType: LoginOption): string[] {
  const configured = process.env.REACT_APP_GOOGLE_REGISTER_PATH?.trim();
  const byRole: Partial<Record<LoginOption, string[]>> = {
    'private client': ['register/PrivateClient/google', 'register/PrivateClient'],
    'business client': ['register/BusinessClient/google', 'register/BusinessClient'],
    'business worker': ['register/BusinessWorker/google', 'register/BusinessWorker'],
    'asset owner': [...ASSET_OWNER_GOOGLE_REGISTER_PATHS],
    'business asset owner': [...ASSET_OWNER_GOOGLE_REGISTER_PATHS],
    'private asset owner': [...ASSET_OWNER_GOOGLE_REGISTER_PATHS],
  };
  const paths = [configured, ...(byRole[loginType] ?? [])].filter((value): value is string =>
    Boolean(value?.trim()),
  );
  return Array.from(new Set(paths));
}

export function googleRegisterPathCandidatesForTests(loginType: LoginOption): string[] {
  return googleRegisterPathCandidates(loginType);
}

let googleScriptPromise: Promise<void> | null = null;

export function loadGoogleIdentityScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Sign-In is only available in the browser'));
  }
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  if (!googleScriptPromise) {
    googleScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector('script[data-offizz-google-gsi="true"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Could not load Google Sign-In')), {
          once: true,
        });
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.dataset.offizzGoogleGsi = 'true';
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Could not load Google Sign-In'));
      document.head.appendChild(script);
    });
  }
  return googleScriptPromise;
}

type GoogleCredentialHandler = (credential: string) => void;

let gsiInitializedClientId: string | null = null;
let gsiCredentialHandler: GoogleCredentialHandler | null = null;

/** Latest Google credential handler — only one GSI `initialize()` per page load. */
export function setGoogleSignInCredentialHandler(handler: GoogleCredentialHandler | null): void {
  gsiCredentialHandler = handler;
}

/** Call once per `clientId`; safe when multiple auth blocks mount on the same page. */
export function ensureGoogleIdentityInitialized(clientId: string): void {
  const google = window.google?.accounts?.id;
  if (!google || !clientId.trim()) {
    return;
  }
  if (gsiInitializedClientId === clientId) {
    return;
  }
  google.initialize({
    client_id: clientId,
    callback: (response) => {
      if (response.credential && gsiCredentialHandler) {
        gsiCredentialHandler(response.credential);
      }
    },
    auto_select: false,
    cancel_on_tap_outside: true,
    itp_support: true,
  });
  gsiInitializedClientId = clientId;
}

/** @internal */
export function resetGoogleIdentityForTests(): void {
  gsiInitializedClientId = null;
  gsiCredentialHandler = null;
  googleScriptPromise = null;
}

export function emailFromGoogleCredential(credential: string): string {
  const payload = decodeJwtPayload(credential);
  return typeof payload?.email === 'string' ? payload.email.trim() : '';
}

export function pictureFromGoogleCredential(credential: string): string | null {
  const payload = decodeJwtPayload(credential);
  const picture = payload?.picture;
  return typeof picture === 'string' && picture.trim() ? picture.trim() : null;
}

export type GoogleIdTokenProfile = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  picture: string | null;
};

function pickGoogleString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Name, email, and photo claims from a Google ID token (OpenID profile). Phone is not included. */
export function googleProfileFromCredential(credential: string): GoogleIdTokenProfile {
  const payload = decodeJwtPayload(credential);
  const email = pickGoogleString(payload?.email) ?? '';
  const givenName = pickGoogleString(payload?.given_name);
  const familyName = pickGoogleString(payload?.family_name);
  const name = pickGoogleString(payload?.name);
  let firstName = givenName;
  let lastName = familyName;
  if (!firstName && !lastName && name) {
    const parts = name.split(/\s+/).filter(Boolean);
    firstName = parts[0] ?? null;
    lastName = parts.slice(1).join(' ') || null;
  }
  const fullName =
    name ??
    ([firstName, lastName].filter(Boolean).join(' ').trim() || null) ??
    (email || null);
  return {
    email,
    firstName,
    lastName,
    fullName,
    picture: pictureFromGoogleCredential(credential),
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Merge Google OpenID claims into a login response when the API omitted them. */
export function enrichAuthBodyWithGoogleProfile(
  body: unknown,
  google: GoogleIdTokenProfile,
): Record<string, unknown> {
  const record = isPlainObject(body) ? { ...body } : {};
  if (google.email && !pickGoogleString(record.email)) {
    record.email = google.email;
  }
  if (google.firstName && !pickGoogleString(record.first_name)) {
    record.first_name = google.firstName;
  }
  if (google.lastName && !pickGoogleString(record.last_name)) {
    record.last_name = google.lastName;
  }
  if (google.fullName && !pickGoogleString(record.name)) {
    record.name = google.fullName;
  }
  if (google.picture && !pickGoogleString(record.profile_image) && !pickGoogleString(record.picture)) {
    record.picture = google.picture;
  }
  return record;
}

export type GoogleLoginApiResult = {
  response: Response;
  urlUsed: string;
  responseText: string;
  parsed: unknown;
  accessToken: string | null;
  pathUsed: string;
  /** `login` or `register` — which flow produced this response. */
  flowUsed: 'login' | 'register';
};

export function googleLoginErrorMessage(result: Pick<GoogleLoginApiResult, 'responseText'>): string {
  if (!result.responseText.trim()) {
    return '';
  }
  try {
    const parsed = JSON.parse(result.responseText) as { message?: unknown };
    return typeof parsed.message === 'string' ? parsed.message.trim() : '';
  } catch {
    return '';
  }
}

/** True when the API says this Google account has never been registered. */
export function isGoogleUserMissingError(result: Pick<GoogleLoginApiResult, 'response' | 'responseText'>): boolean {
  if (result.response.status !== 401) {
    return false;
  }
  const message = googleLoginErrorMessage(result).toLowerCase();
  return /user does not exist|user not found|account not found|no user|not registered/.test(message);
}

/** Register returned conflict — account exists; fall back to role-specific Google login. */
export function isGoogleRegisterConflictError(
  result: Pick<GoogleLoginApiResult, 'response' | 'responseText'>,
): boolean {
  if (result.response.status === 409) {
    return true;
  }
  const message = googleLoginErrorMessage(result).toLowerCase();
  return message.includes('already exists') || message.includes('conflict');
}

export function buildGoogleLoginRequestBody(
  credential: string,
  loginType: LoginOption,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    credential,
    id_token: credential,
    google_id_token: credential,
    provider: 'google',
    login_type: canonicalAssetOwnerLoginType(loginType),
  };
  const email = emailFromGoogleCredential(credential);
  if (email) {
    body.email = email;
    body.identifier = email;
  }
  return body;
}

/** Register endpoints accept profile fields; login/google rejects them (strict DTO). */
export function buildGoogleRegisterRequestBody(
  credential: string,
  loginType: LoginOption,
): Record<string, unknown> {
  const google = googleProfileFromCredential(credential);
  const body = buildGoogleLoginRequestBody(credential, loginType);
  if (google.firstName) {
    body.first_name = google.firstName;
  }
  if (google.lastName) {
    body.last_name = google.lastName;
  }
  body.registration_date = new Date().toISOString();
  return body;
}

/** @deprecated Use buildGoogleLoginRequestBody or buildGoogleRegisterRequestBody. */
export function buildGoogleAuthRequestBody(
  credential: string,
  loginType: LoginOption,
  options?: { includeRegistrationDate?: boolean },
): Record<string, unknown> {
  if (options?.includeRegistrationDate) {
    return buildGoogleRegisterRequestBody(credential, loginType);
  }
  return buildGoogleLoginRequestBody(credential, loginType);
}

async function postGoogleAuthPath(
  path: string,
  body: Record<string, unknown>,
  flowUsed: 'login' | 'register',
): Promise<GoogleLoginApiResult> {
  const anonymousRegister = path.replace(/^\/+/, '').startsWith('register/');
  const { response, urlUsed } = await fetchWithDevProxyFallback(
    path,
    {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
      ...(anonymousRegister ? { credentials: 'omit' as RequestCredentials } : {}),
    },
    devProxyRouteForApiPath(path),
  );
  const responseText = await response.text();
  let parsed: unknown = null;
  if (responseText.trim()) {
    try {
      parsed = JSON.parse(responseText) as unknown;
    } catch {
      parsed = null;
    }
  }
  return {
    response,
    urlUsed,
    responseText,
    parsed,
    accessToken: parsed ? extractAccessTokenFromLoginJson(parsed) : null,
    pathUsed: path,
    flowUsed,
  };
}

function shouldTryNextGoogleLoginPath(result: GoogleLoginApiResult, hasMorePaths: boolean): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (result.response.status === 404) {
    return true;
  }
  return isGoogleUserMissingError(result);
}

function shouldTryNextGoogleRegisterPath(result: GoogleLoginApiResult, hasMorePaths: boolean): boolean {
  if (!hasMorePaths) {
    return false;
  }
  if (result.response.status === 404) {
    return true;
  }
  if (result.response.status === 400 || result.response.status === 409 || result.response.status === 422) {
    return true;
  }
  return false;
}

async function tryGoogleLoginPaths(
  body: Record<string, unknown>,
  paths: string[],
): Promise<{ result: GoogleLoginApiResult | null; sawUserMissing: boolean }> {
  let lastResult: GoogleLoginApiResult | null = null;
  let sawUserMissing = false;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const result = await postGoogleAuthPath(path, body, 'login');
    if (isSuccessfulGoogleLoginResult(result)) {
      return { result, sawUserMissing };
    }
    if (isGoogleUserMissingError(result)) {
      sawUserMissing = true;
    }
    lastResult = result;
    if (shouldTryNextGoogleLoginPath(result, index < paths.length - 1)) {
      continue;
    }
    return { result, sawUserMissing };
  }

  return { result: lastResult, sawUserMissing };
}

/** Login-only Google auth — does not fall back to lite-register (for register-form prefill). */
export async function postGoogleLoginCredentialOnly(
  credential: string,
  loginType: LoginOption,
): Promise<GoogleLoginApiResult> {
  const loginBody = buildGoogleLoginRequestBody(credential, loginType);
  const loginPaths = googleLoginPathCandidates(loginType);
  if (loginPaths.length === 0) {
    throw new Error('No Google login endpoint configured');
  }
  const { result } = await tryGoogleLoginPaths(loginBody, loginPaths);
  if (result) {
    return result;
  }
  throw new Error('No Google login endpoint configured');
}

async function tryGoogleLiteRegister(
  credential: string,
  loginType: LoginOption,
): Promise<GoogleLoginApiResult | null> {
  const paths = googleRegisterPathCandidates(loginType);
  if (paths.length === 0) {
    return null;
  }

  const body = buildGoogleRegisterRequestBody(credential, loginType);
  let lastResult: GoogleLoginApiResult | null = null;

  for (let index = 0; index < paths.length; index += 1) {
    const path = paths[index]!;
    const result = await postGoogleAuthPath(path, body, 'register');
    if (isSuccessfulGoogleLoginResult(result)) {
      return result;
    }
    if (isSuccessfulHttpStatus(result.response.status)) {
      return result;
    }
    lastResult = result;
    if (shouldTryNextGoogleRegisterPath(result, index < paths.length - 1)) {
      continue;
    }
    return result;
  }

  return lastResult;
}

export async function postGoogleLoginCredential(
  credential: string,
  loginType: LoginOption,
): Promise<GoogleLoginApiResult> {
  const loginBody = buildGoogleLoginRequestBody(credential, loginType);
  const loginPaths = googleLoginPathCandidates(loginType);
  if (loginPaths.length === 0) {
    throw new Error('No Google login endpoint configured');
  }

  if (prefersGoogleRegisterFirst(loginType) && supportsGoogleLiteRegister(loginType)) {
    const registerResult = await tryGoogleLiteRegister(credential, loginType);
    if (registerResult && isSuccessfulGoogleLoginResult(registerResult)) {
      return registerResult;
    }

    const loginAfterRegister = await tryGoogleLoginPaths(loginBody, loginPaths);
    if (loginAfterRegister.result && isSuccessfulGoogleLoginResult(loginAfterRegister.result)) {
      return loginAfterRegister.result;
    }

    if (registerResult && isSuccessfulHttpStatus(registerResult.response.status)) {
      return registerResult;
    }
    if (loginAfterRegister.result) {
      return loginAfterRegister.result;
    }
    if (registerResult) {
      return registerResult;
    }
    throw new Error('No Google login endpoint configured');
  }

  const firstPass = await tryGoogleLoginPaths(loginBody, loginPaths);
  if (firstPass.result && isSuccessfulGoogleLoginResult(firstPass.result)) {
    return firstPass.result;
  }

  if (firstPass.sawUserMissing && supportsGoogleLiteRegister(loginType)) {
    const registerResult = await tryGoogleLiteRegister(credential, loginType);
    if (registerResult && isSuccessfulGoogleLoginResult(registerResult)) {
      return registerResult;
    }

    const retryPass = await tryGoogleLoginPaths(loginBody, loginPaths);
    if (retryPass.result && isSuccessfulGoogleLoginResult(retryPass.result)) {
      return retryPass.result;
    }

    if (registerResult && isSuccessfulHttpStatus(registerResult.response.status)) {
      return registerResult;
    }
    if (retryPass.result) {
      return retryPass.result;
    }
    if (registerResult) {
      return registerResult;
    }
  }

  if (firstPass.result) {
    return firstPass.result;
  }
  throw new Error('No Google login endpoint configured');
}

export function extractRefreshTokenFromGoogleLogin(parsed: unknown): string | null {
  const token = extractRefreshTokenFromUnknown(parsed);
  if (token) {
    clearRefreshUnavailable404();
  }
  return token;
}

export function isSuccessfulGoogleLoginResult(result: GoogleLoginApiResult): boolean {
  return isSuccessfulHttpStatus(result.response.status) && Boolean(result.accessToken);
}

export function friendlyGoogleLoginFailureSummary(
  result: GoogleLoginApiResult,
  messages?: {
    notEnabled?: string;
    unavailable?: string;
    loginTypeMismatch?: string;
    userNotRegistered?: string;
  },
): string {
  const apiMessage = googleLoginErrorMessage(result);

  if (result.response.status === 401 && messages?.loginTypeMismatch && /login type/i.test(apiMessage)) {
    return messages.loginTypeMismatch;
  }

  if (isGoogleUserMissingError(result) && messages?.userNotRegistered) {
    return messages.userNotRegistered;
  }

  if (result.response.status === 404) {
    return (
      messages?.notEnabled ??
      'Google sign-in is not enabled on the server yet. Ask your backend team to add a Google login endpoint, or sign in with username and password below.'
    );
  }
  if (result.response.status === 501) {
    return (
      messages?.unavailable ??
      'Google sign-in is not available on this server yet. Use username and password below, or try again later.'
    );
  }
  return friendlyHttpFailureSummary(result.response.status, result.response.statusText, result.responseText);
}

/** Safe summary for error debug panels — never log the raw Google ID token. */
export function googleLoginDebugRequestPayload(
  credential: string,
  loginType: LoginOption,
  result: Pick<GoogleLoginApiResult, 'pathUsed' | 'flowUsed'>,
): Record<string, unknown> {
  const google = googleProfileFromCredential(credential);
  const payload: Record<string, unknown> = {
    provider: 'google',
    login_type: canonicalAssetOwnerLoginType(loginType),
    path: result.pathUsed,
    flow: result.flowUsed,
    credential: '(Google ID token — sent, omitted from logs)',
    email: google.email || undefined,
  };
  if (result.flowUsed === 'register') {
    if (google.firstName) {
      payload.first_name = google.firstName;
    }
    if (google.lastName) {
      payload.last_name = google.lastName;
    }
  } else {
    payload.note =
      'Login sends token + email only. Name/photo from Google are applied to profile after sign-in.';
  }
  return payload;
}
